import { Emitter } from "@core/events/emitter";
import { CampfireActions, type CampfireAction } from "./actionNames";
import { DefaultActionBindings, type KeyBinding } from "./defaultBindings";

/**
 * InputService — unified, intent-rich input for the editor.
 *
 * Replaces direct DOM listeners and the Unity `CampfireInputProvider` /
 * `ISceneInputModel` pair. Feature code (tools, viewport, hierarchy) MUST
 * subscribe to the emitters here instead of attaching `pointerdown` / `keydown`
 * listeners. This mirrors the rule from `Campfire_UI.mdc`:
 *
 *   "ViewModels must not poll raw input like Input.GetMouseButtonDown."
 *
 * Pointer / touch / pen are normalized into a single pointer stream that
 * carries `pointerType`, `pressure`, and the active modifier state at the
 * moment of the event.
 *
 * Keyboard events are translated into named actions through
 * `defaultBindings.ts`. Hard-coded `event.key === "Delete"` checks in feature
 * code are a smell — bind a new action instead.
 */

export type PointerKind = "mouse" | "pen" | "touch";

export interface ModifierState {
  readonly shift: boolean;
  readonly alt: boolean;
  readonly ctrl: boolean;
  readonly meta: boolean;
  /** True when ctrl OR meta is down — cross-platform "command" modifier. */
  readonly metaOrCtrl: boolean;
}

export interface PointerState {
  readonly id: number;
  readonly clientX: number;
  readonly clientY: number;
  readonly x: number;
  readonly y: number;
  readonly button: number;
  readonly buttons: number;
  readonly kind: PointerKind;
  readonly pressure: number;
  readonly modifiers: ModifierState;
  readonly timestamp: number;
}

export interface PointerMoveState extends PointerState {
  readonly dx: number;
  readonly dy: number;
}

export interface WheelState {
  readonly x: number;
  readonly y: number;
  readonly deltaX: number;
  readonly deltaY: number;
  readonly modifiers: ModifierState;
}

export interface KeyState {
  readonly code: string;
  readonly key: string;
  readonly modifiers: ModifierState;
  readonly repeat: boolean;
}

export interface ActionEvent {
  readonly action: CampfireAction;
  readonly modifiers: ModifierState;
}

const DEFAULT_MODIFIERS: ModifierState = Object.freeze({
  shift: false,
  alt: false,
  ctrl: false,
  meta: false,
  metaOrCtrl: false,
});

/**
 * Read modifiers off any DOM event that carries `*Key` flags.
 */
function modifiersFrom(ev: KeyboardEvent | MouseEvent | PointerEvent | WheelEvent): ModifierState {
  return {
    shift: ev.shiftKey,
    alt: ev.altKey,
    ctrl: ev.ctrlKey,
    meta: ev.metaKey,
    metaOrCtrl: ev.ctrlKey || ev.metaKey,
  };
}

/**
 * True when the keystroke belongs to a text field rather than to the editor.
 *
 * The action layer is the only thing gated on this: raw `onKeyDown` /
 * `onKeyUp` still fire, because a text UI may want them. Without the gate,
 * every single-letter and ⌘-letter binding would double as an editor command
 * while typing — `F` in the Spark prompt would reframe the camera, `Delete`
 * would delete the selected model, and ⌘C would copy a model instead of the
 * selected text. Unity gets this for free: its shortcuts come from
 * `CampfireInputProvider`, which stops reporting while an `InputField` has
 * focus.
 */
function isTextEntryTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  if (tag === "TEXTAREA" || tag === "SELECT") return true;
  if (tag !== "INPUT") return false;
  // Non-text inputs (checkbox, range, colour, radio) are controls, not text —
  // the editor's shortcuts should keep working while one of them has focus.
  const type = (target as HTMLInputElement).type;
  return type !== "checkbox" && type !== "radio" && type !== "range"
    && type !== "color" && type !== "button" && type !== "submit";
}

function bindingMatches(b: KeyBinding, ev: KeyboardEvent): boolean {
  if (b.code !== ev.code) return false;
  if (b.shift !== undefined && b.shift !== ev.shiftKey) return false;
  if (b.alt !== undefined && b.alt !== ev.altKey) return false;
  if (b.metaOrCtrl !== undefined) {
    const has = ev.ctrlKey || ev.metaKey;
    if (b.metaOrCtrl !== has) return false;
  }
  return true;
}

export class InputService {
  readonly onPointerDown = new Emitter<PointerState>();
  readonly onPointerMove = new Emitter<PointerMoveState>();
  readonly onPointerUp = new Emitter<PointerState>();
  readonly onPointerCancel = new Emitter<PointerState>();
  readonly onContextMenu = new Emitter<PointerState>();
  readonly onWheel = new Emitter<WheelState>();
  readonly onKeyDown = new Emitter<KeyState>();
  readonly onKeyUp = new Emitter<KeyState>();
  /** High-level intent fired when a bound action triggers (key down phase). */
  readonly onAction = new Emitter<ActionEvent>();
  /** Modifier state bus for code that needs the "is shift held right now" answer. */
  readonly onModifiersChange = new Emitter<ModifierState>();

  private m_target: HTMLElement | null = null;
  private m_modifiers: ModifierState = DEFAULT_MODIFIERS;
  private m_lastPointerByPointerId = new Map<number, { x: number; y: number }>();
  private readonly m_bindings: Record<CampfireAction, readonly KeyBinding[]>;

  constructor(bindings: Partial<Record<CampfireAction, readonly KeyBinding[]>> = {}) {
    const merged = { ...DefaultActionBindings } as Record<CampfireAction, readonly KeyBinding[]>;
    for (const key of Object.keys(bindings) as CampfireAction[]) {
      const value = bindings[key];
      if (value) merged[key] = value;
    }
    this.m_bindings = merged;
  }

  /**
   * Attach to a target element (typically the Three.js canvas) plus the global
   * window for keyboard. Idempotent: re-attaching to a new target detaches the
   * previous one.
   */
  attach(target: HTMLElement): void {
    if (this.m_target === target) return;
    this.detach();
    this.m_target = target;

    target.addEventListener("pointerdown", this.handlePointerDown);
    target.addEventListener("pointermove", this.handlePointerMove);
    target.addEventListener("pointerup", this.handlePointerUp);
    target.addEventListener("pointercancel", this.handlePointerCancel);
    target.addEventListener("pointerleave", this.handlePointerCancel);
    target.addEventListener("contextmenu", this.handleContextMenu);
    target.addEventListener("wheel", this.handleWheel, { passive: false });

    window.addEventListener("keydown", this.handleKeyDown);
    window.addEventListener("keyup", this.handleKeyUp);
    window.addEventListener("blur", this.handleBlur);
  }

  detach(): void {
    const target = this.m_target;
    if (!target) return;
    target.removeEventListener("pointerdown", this.handlePointerDown);
    target.removeEventListener("pointermove", this.handlePointerMove);
    target.removeEventListener("pointerup", this.handlePointerUp);
    target.removeEventListener("pointercancel", this.handlePointerCancel);
    target.removeEventListener("pointerleave", this.handlePointerCancel);
    target.removeEventListener("contextmenu", this.handleContextMenu);
    target.removeEventListener("wheel", this.handleWheel);
    window.removeEventListener("keydown", this.handleKeyDown);
    window.removeEventListener("keyup", this.handleKeyUp);
    window.removeEventListener("blur", this.handleBlur);
    this.m_target = null;
    this.m_lastPointerByPointerId.clear();
  }

  get modifiers(): ModifierState {
    return this.m_modifiers;
  }

  /** True if the named action is currently held down (modifier-style actions). */
  isActionHeld(action: CampfireAction): boolean {
    if (action === CampfireActions.Multiselect) return this.m_modifiers.shift;
    if (action === CampfireActions.Rangeselect) return this.m_modifiers.metaOrCtrl;
    return false;
  }

  private handlePointerDown = (ev: PointerEvent): void => {
    this.updateModifiersFromEvent(ev);
    const state = this.toPointerState(ev);
    this.m_lastPointerByPointerId.set(ev.pointerId, { x: state.x, y: state.y });
    this.onPointerDown.emit(state);
  };

  private handlePointerMove = (ev: PointerEvent): void => {
    this.updateModifiersFromEvent(ev);
    const last = this.m_lastPointerByPointerId.get(ev.pointerId);
    const state = this.toPointerState(ev);
    const dx = last ? state.x - last.x : 0;
    const dy = last ? state.y - last.y : 0;
    this.m_lastPointerByPointerId.set(ev.pointerId, { x: state.x, y: state.y });
    this.onPointerMove.emit({ ...state, dx, dy });
  };

  private handlePointerUp = (ev: PointerEvent): void => {
    this.updateModifiersFromEvent(ev);
    const state = this.toPointerState(ev);
    this.m_lastPointerByPointerId.delete(ev.pointerId);
    this.onPointerUp.emit(state);
  };

  private handlePointerCancel = (ev: PointerEvent): void => {
    const state = this.toPointerState(ev);
    this.m_lastPointerByPointerId.delete(ev.pointerId);
    this.onPointerCancel.emit(state);
  };

  private handleContextMenu = (ev: MouseEvent): void => {
    ev.preventDefault();
    this.updateModifiersFromEvent(ev);
    this.onContextMenu.emit(this.toMousePointerState(ev));
  };

  private handleWheel = (ev: WheelEvent): void => {
    this.updateModifiersFromEvent(ev);
    this.onWheel.emit({
      x: ev.clientX,
      y: ev.clientY,
      deltaX: ev.deltaX,
      deltaY: ev.deltaY,
      modifiers: modifiersFrom(ev),
    });
  };

  private handleKeyDown = (ev: KeyboardEvent): void => {
    this.updateModifiersFromEvent(ev);
    const state: KeyState = {
      code: ev.code,
      key: ev.key,
      modifiers: modifiersFrom(ev),
      repeat: ev.repeat,
    };
    this.onKeyDown.emit(state);
    if (ev.repeat) return;
    if (isTextEntryTarget(ev.target)) return;
    for (const action of Object.keys(this.m_bindings) as CampfireAction[]) {
      const bindings = this.m_bindings[action];
      if (bindings.some((b) => bindingMatches(b, ev))) {
        this.onAction.emit({ action, modifiers: state.modifiers });
      }
    }
  };

  private handleKeyUp = (ev: KeyboardEvent): void => {
    this.updateModifiersFromEvent(ev);
    this.onKeyUp.emit({
      code: ev.code,
      key: ev.key,
      modifiers: modifiersFrom(ev),
      repeat: ev.repeat,
    });
  };

  /** Drop modifier state when the window loses focus so we don't get stuck "shift held" bugs. */
  private handleBlur = (): void => {
    this.setModifiers(DEFAULT_MODIFIERS);
    this.m_lastPointerByPointerId.clear();
  };

  private toPointerState(ev: PointerEvent): PointerState {
    const target = ev.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    return {
      id: ev.pointerId,
      clientX: ev.clientX,
      clientY: ev.clientY,
      x: ev.clientX - rect.left,
      y: ev.clientY - rect.top,
      button: ev.button,
      buttons: ev.buttons,
      kind: (ev.pointerType as PointerKind) || "mouse",
      pressure: ev.pressure,
      modifiers: modifiersFrom(ev),
      timestamp: ev.timeStamp,
    };
  }

  private toMousePointerState(ev: MouseEvent): PointerState {
    const target = ev.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    return {
      id: -1,
      clientX: ev.clientX,
      clientY: ev.clientY,
      x: ev.clientX - rect.left,
      y: ev.clientY - rect.top,
      button: ev.button,
      buttons: ev.buttons,
      kind: "mouse",
      pressure: 0,
      modifiers: modifiersFrom(ev),
      timestamp: ev.timeStamp,
    };
  }

  private updateModifiersFromEvent(ev: KeyboardEvent | MouseEvent | PointerEvent | WheelEvent): void {
    this.setModifiers(modifiersFrom(ev));
  }

  private setModifiers(next: ModifierState): void {
    const prev = this.m_modifiers;
    if (
      prev.shift === next.shift &&
      prev.alt === next.alt &&
      prev.ctrl === next.ctrl &&
      prev.meta === next.meta
    ) {
      return;
    }
    this.m_modifiers = next;
    this.onModifiersChange.emit(next);
  }
}

export const INPUT_SERVICE = Symbol.for("InputService");
